export const EVALUATION_VISIBLE_STATUSES = [
	"EVALUATION",
	"AWARDED",
	"COMPLETE",
] as const;

export const PANEL_MODES = {
	SOLO_MANUAL: "manual_solo",
	SOLO_AI: "ai_solo",
	PANEL_MANUAL: "manual_panel",
	PANEL_AI: "ai_panel",
};

export const EVALUATION_STEP_IDS = {
	PRE_COMPLIANCE: 0,
	CRITERIA: 1,
	PANEL: 2,
	INITIAL: 3,
	CLARIFICATIONS: 4,
	REVISIONS: 5,
	PANEL_MEETING: 6,
	FINAL: 7,
} as const;

export const EVALUATION_STEP_NUMBERS = {
	CRITERIA: 0,
	PANEL: 1,
	INITIAL: 2,
	CLARIFICATIONS: 3,
	REVISIONS: 4,
	PANEL_MEETING: 5,
	FINAL: 6,
} as const;

/** Steps included in solo workflow (no Panel setup step). */
export const SOLO_WORKFLOW_STEPS = [
	EVALUATION_STEP_IDS.PRE_COMPLIANCE,
	EVALUATION_STEP_IDS.CRITERIA,
	EVALUATION_STEP_IDS.INITIAL,
	EVALUATION_STEP_IDS.CLARIFICATIONS,
	EVALUATION_STEP_IDS.REVISIONS,
	EVALUATION_STEP_IDS.PANEL_MEETING,
	EVALUATION_STEP_IDS.FINAL,
] as const;

/** Steps included in panel workflow (all steps). */
export const PANEL_WORKFLOW_STEPS = [
	EVALUATION_STEP_IDS.PRE_COMPLIANCE,
	EVALUATION_STEP_IDS.CRITERIA,
	EVALUATION_STEP_IDS.PANEL,
	EVALUATION_STEP_IDS.INITIAL,
	EVALUATION_STEP_IDS.CLARIFICATIONS,
	EVALUATION_STEP_IDS.REVISIONS,
	EVALUATION_STEP_IDS.PANEL_MEETING,
	EVALUATION_STEP_IDS.FINAL,
] as const;

/**
 * A step's help text. Either a single string (identical for every workflow)
 * or a per-mode pair so solo workflows don't show panel-centric wording (PRO-467).
 */
export type StepDescription = string | { solo: string; panel: string };

export const EVALUATION_STEP_META: ReadonlyArray<{
	id: number;
	name: string;
	description: StepDescription;
}> = [
	{
		id: EVALUATION_STEP_IDS.PRE_COMPLIANCE,
		name: "Pre-Compliance",
		description:
			"Review and confirm compliance questions before evaluating supplier responses",
	},
	{
		id: EVALUATION_STEP_IDS.CRITERIA,
		name: "Evaluation Criteria",
		description:
			"Outline your criteria, with search for previous criteria to quick add",
	},
	{
		id: EVALUATION_STEP_IDS.PANEL,
		name: "Evaluation Panel",
		description:
			"Setup your evaluation, choose to use AI or just score yourself",
	},
	{
		id: EVALUATION_STEP_IDS.INITIAL,
		name: "Initial Evaluation",
		description: {
			panel:
				"Getting weights, scores and clarifications back from the panel",
			solo:
				"Score each supplier against your criteria and raise any clarifications",
		},
	},
	{
		id: EVALUATION_STEP_IDS.CLARIFICATIONS,
		name: "Clarification Review",
		description: {
			panel:
				"Consolidated list of clarifications from all panel members sent to suppliers",
			solo:
				"Review your clarifications before they are sent to suppliers",
		},
	},
	{
		id: EVALUATION_STEP_IDS.REVISIONS,
		name: "Revisions",
		description:
			"Any revision requests will be logged here and summaries provided",
	},
	{
		id: EVALUATION_STEP_IDS.PANEL_MEETING,
		name: "Final Scoring",
		description: {
			panel: "Panel comes together to discuss and finalise scoring",
			solo: "Review and finalise your scores",
		},
	},
	{
		id: EVALUATION_STEP_IDS.FINAL,
		name: "Final Report & Award",
		description:
			"Review ranked results, record award decisions and generate the final report",
	},
];

/** Resolve a step's help text for the active workflow (PRO-467). */
export const resolveStepDescription = (
	description: StepDescription,
	isSolo: boolean
): string => {
	if (typeof description === "string") {
		return description;
	}
	return isSolo ? description.solo : description.panel;
};
